import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight } from 'lucide-react';

const Vision = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-mist">
      <header className="border-b border-calm-border bg-fern/80 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-6 py-5 flex justify-between items-center">
          <div 
            className="text-2xl font-semibold text-calm-text cursor-pointer"
            onClick={() => navigate('/')}
          >
            OHMNAI
          </div>
          <Button
            variant="outline"
            onClick={() => navigate('/')}
            className="border-calm-border text-forest hover:bg-fern"
          >
            Back to Home
          </Button>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-20">
        <h1 className="text-5xl font-light text-calm-text mb-12">A future worth enabling</h1>

        <section className="mb-16">
          <h2 className="text-2xl font-light text-calm-text mb-6">Today: Alignment engine for individuals</h2>
          <div className="bg-fern rounded-xl p-8 border border-calm-border space-y-4">
            <p className="text-lg text-forest leading-relaxed">
              OHMNAI starts here: one person, their values, their capacity, one action at a time.
            </p>
            <p className="text-lg text-forest leading-relaxed">
              No feeds. No persuasion. No extraction. Just calm alignment between intention and action.
            </p>
            <p className="text-lg text-forest leading-relaxed">
              This is the foundation. It has to work quietly, honestly, and without coercion—or nothing else matters.
            </p>
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-light text-calm-text mb-6">Tomorrow: Coordinated capacity</h2>
          <div className="bg-fern rounded-xl p-8 border border-calm-border space-y-4">
            <p className="text-lg text-forest leading-relaxed">
              When many people act on aligned values, patterns emerge. Not through centralized control, but through transparent coordination.
            </p>
            <p className="text-lg text-forest leading-relaxed">
              Circle members can pool actions: "If 100 of us care about watershed protection and have capacity this month, here's something we can do together."
            </p>
            <p className="text-lg text-forest leading-relaxed">
              Still no persuasion. Still no gamification. Just visible collective capacity.
            </p>
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-light text-calm-text mb-6">The horizon: Alignment mesh</h2>
          <div className="bg-fern rounded-xl p-8 border border-calm-border space-y-4">
            <p className="text-lg text-forest leading-relaxed">
              What if organizations could request aligned action without manipulating?
            </p>
            <p className="text-lg text-forest leading-relaxed">
              "We're a watershed nonprofit. We need 50 petition signatures this month. OHMNAI, surface this to people who care about local water + have 5 minutes."
            </p>
            <p className="text-lg text-forest leading-relaxed">
              Users still skip. Still control boundaries. But the mesh becomes infrastructure for values-aligned coordination at scale.
            </p>
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-light text-calm-text mb-6">Ethical agents</h2>
          <div className="bg-fern rounded-xl p-8 border border-calm-border space-y-4">
            <p className="text-lg text-forest leading-relaxed">
              AI that explains alignment, not optimizes behavior. Agents that respect capacity, boundaries, and silence.
            </p>
            <p className="text-lg text-forest leading-relaxed">
              Not "how to maximize your impact" (extractive framing) but "here's what fits your values and capacity right now" (respectful framing).
            </p>
            <p className="text-lg text-forest leading-relaxed">
              The technical architecture for this exists. What's missing is the will to build it without dark patterns.
            </p>
          </div>
        </section>

        <section className="mb-16">
          <div className="bg-gradient-to-br from-forest to-forest-dark rounded-2xl p-12 text-white">
            <p className="text-xl font-light leading-relaxed mb-6">
              "Who you are is what you choose to enable."
            </p>
            <p className="text-lg text-herbal-light leading-relaxed">
              This is not a slogan. It's the system promise: OHMNAI opens the door, then steps back. You choose what to enable—with your time, your money, your attention.
            </p>
            <p className="text-lg text-herbal-light leading-relaxed mt-4">
              The platform never decides for you. It just makes the path visible.
            </p>
          </div>
        </section>

        <div className="flex justify-center pt-8">
          <Button
            onClick={() => navigate('/values-profile')}
            className="bg-forest hover:bg-forest-dark text-white px-8 py-6 text-base rounded-lg transition-colors"
          >
            Start your journey <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Vision;
